import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Loader2, Download, Copy, Check, AlertTriangle,
  UserPlus, ShoppingBag, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  provisioned: "text-[color:var(--auditiq-success,#5bbf85)]",
  processing:  "text-gold",
  failed:      "text-[color:var(--auditiq-critical)]",
};

const WixSubscribersCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/admin/wix/subscribers")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load subscribers"))
      .finally(() => setLoading(false));
  }, []);

  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/wix/subscribers.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "wix_subscribers.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const copyField = async (value, idx, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(`${idx}-${label}`);
      setTimeout(() => setCopied(null), 1800);
    } catch { toast.error("Copy failed — select manually."); }
  };

  if (loading) {
    return (
      <div className="glass p-6 flex items-center gap-2 text-cream-dim">
        <Loader2 size={16} className="animate-spin" /> Loading Wix subscribers…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="glass p-6" data-testid="wix-subscribers-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
            <ShoppingBag size={12} /> Wix Subscribers
          </div>
          <h3 className="font-display text-xl text-cream mt-1">Subscription Provisioning</h3>
          <div className="text-[11px] text-cream-dim mt-1">
            Customers from Wix webhook · MOCKED email (temp password shown below for manual share)
          </div>
        </div>
        <Button onClick={downloadCsv} className="btn-ghost-gold h-9 px-4 text-xs"
                data-testid="wix-subscribers-csv">
          <Download size={12} className="mr-2" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mt-5">
        <div className="p-3 rounded bg-navy-deep border border-gold-dim/20">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim">Total</div>
          <div className="metric text-2xl text-gold mt-1" data-testid="wix-stat-total">{data.stats?.total || 0}</div>
        </div>
        <div className="p-3 rounded bg-navy-deep border border-gold-dim/20">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim">Provisioned</div>
          <div className="metric text-2xl text-[color:var(--auditiq-success,#5bbf85)] mt-1" data-testid="wix-stat-provisioned">
            {data.stats?.provisioned || 0}
          </div>
        </div>
        <div className="p-3 rounded bg-navy-deep border border-gold-dim/20">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim">Failed</div>
          <div className="metric text-2xl text-[color:var(--auditiq-critical)] mt-1" data-testid="wix-stat-failed">
            {data.stats?.failed || 0}
          </div>
        </div>
        <div className="p-3 rounded bg-navy-deep border border-gold-dim/20">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim">Email MOCKED</div>
          <div className="metric text-2xl text-gold mt-1" data-testid="wix-stat-mocked">{data.stats?.email_mocked || 0}</div>
        </div>
      </div>

      {/* Setup instructions when empty */}
      {data.subscribers.length === 0 && (
        <div className="mt-5 p-4 rounded bg-gold/5 border border-gold/30 text-sm text-cream-dim">
          <div className="flex items-start gap-2">
            <UserPlus size={16} className="text-gold mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-gold font-medium mb-1">No Wix subscribers yet</div>
              Configure your Wix Automation to POST to:{" "}
              <code className="text-electric font-mono text-xs break-all">
                {window.location.origin.replace(":3000", "")}/api/webhooks/wix/subscription
              </code>
              <div className="mt-2 text-xs">
                Include header <code className="text-gold font-mono">X-Wix-Secret</code> matching your
                <code className="text-gold font-mono"> WIX_WEBHOOK_SECRET</code> env var. Payload should include
                email, first/last name, company, planId, planName, and orderId.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscribers table */}
      {data.subscribers.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="wix-subscribers-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.18em] text-cream-dim border-b border-gold-dim/30">
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Organization</th>
                <th className="text-left px-3 py-2">Plan</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Temp PW</th>
              </tr>
            </thead>
            <tbody>
              {data.subscribers.map((s, i) => (
                <tr key={s.log_id || i} className="border-b border-gold-dim/10"
                    data-testid={`wix-sub-${i}`}>
                  <td className="px-3 py-2 text-cream-dim text-xs">
                    {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-gold font-mono text-xs">
                    <div className="flex items-center gap-1">
                      {s.email}
                      <a href={`mailto:${s.email}`} className="text-cream-dim hover:text-gold">
                        <ExternalLink size={10} />
                      </a>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-cream">{s.organization || "—"}</td>
                  <td className="px-3 py-2 text-cream-dim">{s.plan_name || s.plan_id || "—"}</td>
                  <td className="px-3 py-2 text-cream-dim">{s.role || "—"}</td>
                  <td className={`px-3 py-2 ${STATUS_COLOR[s.status] || "text-cream-dim"}`}>
                    {s.status === "failed" && <AlertTriangle size={12} className="inline mr-1" />}
                    {s.status}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {s.temporary_password ? (
                      <div className="inline-flex items-center gap-1">
                        <code className="font-mono text-[10px] bg-navy-deep px-2 py-0.5 rounded text-gold">
                          {s.temporary_password}
                        </code>
                        <button
                          onClick={() => copyField(s.temporary_password, i, "pw")}
                          className="text-electric hover:text-gold"
                          data-testid={`wix-sub-copy-pw-${i}`}
                          title="Copy temp password"
                        >
                          {copied === `${i}-pw` ? <Check size={11} /> : <Copy size={11} />}
                        </button>
                        {s.setup_url && (
                          <button
                            onClick={() => copyField(s.setup_url, i, "link")}
                            className="text-electric hover:text-gold ml-1"
                            data-testid={`wix-sub-copy-link-${i}`}
                            title="Copy setup link"
                          >
                            {copied === `${i}-link` ? <Check size={11} /> : <span className="text-[10px]">link</span>}
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-cream-dim text-xs">{s.error_message || "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WixSubscribersCard;
